/**
 * Capabilities page — four-level capability repository.
 *
 * Hierarchy:
 *   Tenure → Title(s) → Experience(s) → Capability bullets
 *
 * Layout: every row is LEFT (content) + RIGHT (controls).
 *
 * Drag-and-drop (dnd-kit): a single DndContext per tenure card handles all
 * three drag types — titles (within tenure), experiences (within title), and
 * capability bullets (within or across experiences in the same tenure).
 * onDragEnd dispatches by type using Set membership lookups. Cross-tenure
 * and cross-title moves are not permitted.
 *
 * Every row uses the same flat flex structure with consistent pr-3:
 *   [⠿ shrink-0] [• shrink-0] [body flex-1] [metadata w-52 shrink-0] [✏🗑 shrink-0]
 * Icons are always the last unnested flex child so they land at the same
 * x-column regardless of how wide the actions or metadata column is.
 */
import { useState, useEffect, useRef, useImperativeHandle, forwardRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import type { DragDropEventHandlers } from '@dnd-kit/react';
import { arrayMove, move } from '@dnd-kit/helpers';
import { Feedback, PointerSensor, KeyboardSensor } from '@dnd-kit/dom';
import {
  Plus, Pencil, Trash2, X, FolderPlus, Briefcase, List, GripVertical, Sparkles, GraduationCap,
} from 'lucide-react';
import { tenureApi, titleApi, experienceApi, capabilityApi, authApi, educationApi, creditsApi, CREDIT_COSTS } from '@/lib/api';
import { createApiSkillRepository } from '@/lib/skillRepository';
import type { Tenure, Title, Experience, Capability, UpdateCapability, ReorderItem, ReorderTitleItem, ReorderExperienceItem, Education, ReorderEducationItem } from '@/lib/api';
import type { ViewPreferences } from '@resume-builder/shared';
import { Button } from '@/components/ui/button';
import { Navigation } from '@/components/Navigation';
import { TenureModal } from '@/components/TenureModal';
import { EducationModal } from '@/components/EducationModal';
import { BulletRewritePanel } from '@/components/BulletRewritePanel';
import { SkillBucketsSection } from '@/components/SkillBucketsSection';
import { CreditGate } from '@/components/CreditGate';
import { showErrorToast } from '@/lib/toast';

// ── Types ─────────────────────────────────────────────────────────────────────

interface FlatItem {
  id: string;
  type: 'title' | 'experience' | 'capability';
  flatIndex: number;
  parentId?: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDateRange(startDate: string | null, endDate: string | null): string {
  if (!startDate && !endDate) return '';
  const fmt = (d: string) => {
    const [year = '', month = ''] = d.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[parseInt(month, 10) - 1] ?? month} ${year}`;
  };
  if (startDate && endDate) return `${fmt(startDate)} – ${fmt(endDate)}`;
  if (startDate) return `${fmt(startDate)} – present`;
  return '';
}

function experienceLabel(exp: Experience): string {
  return [exp.project, exp.team, exp.role].filter(Boolean).join(' · ');
}

function latestTitle(titles: Title[]): Title | undefined {
  if (titles.length === 0) return undefined;
  return [...titles].sort((a, b) => {
    if (a.startDate && b.startDate) return b.startDate.localeCompare(a.startDate);
    if (a.startDate) return -1;
    if (b.startDate) return 1;
    return b.createdAt.localeCompare(a.createdAt);
  })[0];
}

// Thin pipe separator between control groups
function Sep() {
  return <span className="text-border/60 select-none mx-0.5 text-sm">|</span>;
}

// ── Imperative handle exposed by ExperienceSubSection ────────────────────────
// Parent heading rows call these when add-bullet buttons live outside the exp.

interface ExperienceHandle {
  openInline: () => void;
  openBulk:   () => void;
}

// ── Modal state ────────────────────────────────────────────────────────────────

interface ModalState {
  open: boolean;
  tenure?: Tenure;
  title?: Title;
  experience?: Experience;
  tenureId?: string;
  tenureCompany?: string;
  titleId?: string;
  titleText?: string;
}
const CLOSED: ModalState = { open: false };

// ── Page ───────────────────────────────────────────────────────────────────────

export default function CapabilitiesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const apiRepo = useMemo(() => createApiSkillRepository(queryClient), [queryClient]);
  const [modal, setModal] = useState<ModalState>(CLOSED);
  const [eduModal, setEduModal] = useState<{ open: boolean; education?: Education }>({ open: false });

  const { data: authData, isLoading: authLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authApi.me,
    retry: false,
  });

  if (!authLoading && !authData) {
    void navigate('/login');
    return null;
  }

  const { data: tenures = [], isLoading: tenuresLoading } = useQuery({
    queryKey: ['tenures'],
    queryFn: tenureApi.list,
    enabled: !!authData,
  });

  const { data: titles = [] } = useQuery({
    queryKey: ['titles'],
    queryFn: () => titleApi.list(),
    enabled: !!authData,
  });

  const { data: experiences = [] } = useQuery({
    queryKey: ['experiences'],
    queryFn: experienceApi.list,
    enabled: !!authData,
  });

  const { data: capData } = useQuery({
    queryKey: ['capabilities'],
    queryFn: () => capabilityApi.list({ limit: 200 }),
    enabled: !!authData,
  });

  const { data: educationEntries = [] } = useQuery({
    queryKey: ['education'],
    queryFn: educationApi.list,
    enabled: !!authData,
  });

  const { data: plan } = useQuery({
    queryKey: ['credits', 'balance'],
    queryFn: creditsApi.getBalance,
    enabled: !!authData,
    staleTime: 60_000,
  });

  const allCaps = capData?.items ?? [];

  const titlesByTenure = titles.reduce<Record<string, Title[]>>((acc, t) => {
    (acc[t.tenureId] ??= []).push(t); return acc;
  }, {});
  const expsByTitle = experiences.reduce<Record<string, Experience[]>>((acc, e) => {
    (acc[e.titleId] ??= []).push(e); return acc;
  }, {});
  const capsByExp = allCaps.reduce<Record<string, Capability[]>>((acc, c) => {
    (acc[c.experienceId] ??= []).push(c); return acc;
  }, {});

  // ── Mutations ──────────────────────────────────────────────────────────────

  const deleteEducation = useMutation({
    mutationFn: educationApi.delete,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['education'] }),
  });

  const deleteTenure = useMutation({
    mutationFn: tenureApi.delete,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenures'] });
      void queryClient.invalidateQueries({ queryKey: ['titles'] });
      void queryClient.invalidateQueries({ queryKey: ['experiences'] });
      void queryClient.invalidateQueries({ queryKey: ['capabilities'] });
      void queryClient.invalidateQueries({ queryKey: ['skillBuckets'] });
    },
  });
  const deleteTitle = useMutation({
    mutationFn: titleApi.delete,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['titles'] });
      void queryClient.invalidateQueries({ queryKey: ['experiences'] });
      void queryClient.invalidateQueries({ queryKey: ['capabilities'] });
      void queryClient.invalidateQueries({ queryKey: ['skillBuckets'] });
    },
  });
  const deleteExperience = useMutation({
    mutationFn: experienceApi.delete,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['experiences'] });
      void queryClient.invalidateQueries({ queryKey: ['capabilities'] });
      void queryClient.invalidateQueries({ queryKey: ['skillBuckets'] });
    },
  });
  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => { queryClient.clear(); void navigate('/login'); },
  });

  const updateViewPrefsMutation = useMutation({
    mutationFn: (prefs: ViewPreferences) => authApi.updateViewPreferences(prefs),
    onSuccess: (updated) => {
      queryClient.setQueryData(['auth', 'me'], updated);
    },
  });

  function handleToggleViewPref(key: keyof ViewPreferences) {
    const current = authData?.user.viewPreferences ?? { collapseTitles: false, collapseProjects: false };
    updateViewPrefsMutation.mutate({ ...current, [key]: !current[key] });
  }

  // ── Modal helpers ──────────────────────────────────────────────────────────

  const openCreateNew   = () => setModal({ open: true });
  const openEditTenure  = (t: Tenure, titleToEdit?: Title) => setModal({ open: true, tenure: t, title: titleToEdit });
  const openEditTitle   = (t: Title)      => setModal({ open: true, title: t });
  const openEditExp     = (e: Experience) => setModal({ open: true, experience: e });
  const openAddTitle    = (t: Tenure)     => setModal({ open: true, tenureId: t.id, tenureCompany: t.company });
  const openAddExp      = (t: Title)      => setModal({ open: true, titleId: t.id, titleText: t.title });

  const handleDeleteTenure = (t: Tenure) => {
    const ts = titlesByTenure[t.id] ?? [];
    const expCount = ts.reduce((n, ti) => n + (expsByTitle[ti.id] ?? []).length, 0);
    const bulletCount = ts.reduce(
      (n, ti) => n + (expsByTitle[ti.id] ?? []).reduce((m, e) => m + (capsByExp[e.id] ?? []).length, 0), 0,
    );
    const warn = bulletCount > 0
      ? ` This will also delete ${expCount} project${expCount === 1 ? '' : 's'} and ${bulletCount} bullet${bulletCount === 1 ? '' : 's'}.`
      : '';
    if (window.confirm(`Delete "${t.company}"?${warn} This cannot be undone.`))
      deleteTenure.mutate(t.id);
  };
  const handleDeleteTitle = (t: Title, company: string) => {
    const exps = expsByTitle[t.id] ?? [];
    const bulletCount = exps.reduce((n, e) => n + (capsByExp[e.id] ?? []).length, 0);
    const warn = bulletCount > 0
      ? ` This will also delete ${exps.length} project${exps.length === 1 ? '' : 's'} and ${bulletCount} bullet${bulletCount === 1 ? '' : 's'}.`
      : '';
    if (window.confirm(`Delete title "${t.title}" at ${company}?${warn} This cannot be undone.`))
      deleteTitle.mutate(t.id);
  };
  const handleDeleteExp = (exp: Experience) => {
    const count = capsByExp[exp.id]?.length ?? 0;
    const label = experienceLabel(exp) || 'this project';
    const warn = count > 0 ? ` This will also delete ${count} bullet${count === 1 ? '' : 's'}.` : '';
    if (window.confirm(`Delete "${label}"?${warn} This cannot be undone.`))
      deleteExperience.mutate(exp.id);
  };

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Persistent navigation */}
      <Navigation />

      <main className="mx-auto max-w-5xl px-4 py-6 space-y-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            {([
              { key: 'collapseTitles' as const, label: 'Titles' },
              { key: 'collapseProjects' as const, label: 'Projects' },
            ]).map(({ key, label }) => {
              const active = authData?.user.viewPreferences?.[key] ?? false;
              return (
                <button
                  key={key}
                  onClick={() => handleToggleViewPref(key)}
                  disabled={updateViewPrefsMutation.isPending}
                  className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs transition-colors disabled:opacity-50 ${
                    active
                      ? 'bg-muted text-foreground font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                  }`}
                  title={active ? `Expand ${label.toLowerCase()}` : `Collapse ${label.toLowerCase()}`}
                >
                  {active ? `${label} collapsed` : `Collapse ${label.toLowerCase()}`}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEduModal({ open: true })}>
              <GraduationCap className="h-4 w-4 mr-1.5" />Add education
            </Button>
            <Button onClick={openCreateNew}><Plus className="h-4 w-4 mr-1.5" />Add experience</Button>
          </div>
        </div>

        {tenuresLoading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <div key={i} className="h-24 rounded-lg border bg-muted/30 animate-pulse" />)}
          </div>
        )}

        {!tenuresLoading && tenures.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-16 text-center">
            <p className="text-lg font-medium">No experiences yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Add a company, your official title, then capability bullets.
            </p>
            <Button className="mt-4" onClick={openCreateNew}>
              <Plus className="h-4 w-4 mr-1.5" />Add your first experience
            </Button>
          </div>
        )}

        {tenures.map((tenure) => (
          <TenureSection
            key={tenure.id}
            tenure={tenure}
            titles={titlesByTenure[tenure.id] ?? []}
            expsByTitle={expsByTitle}
            capsByExp={capsByExp}
            collapseTitles={authData?.user.viewPreferences?.collapseTitles ?? false}
            collapseProjects={authData?.user.viewPreferences?.collapseProjects ?? false}
            onEditTenure={openEditTenure}
            onDeleteTenure={handleDeleteTenure}
            onAddTitle={openAddTitle}
            onEditTitle={openEditTitle}
            onDeleteTitle={handleDeleteTitle}
            onAddExperience={openAddExp}
            onEditExperience={openEditExp}
            onDeleteExperience={handleDeleteExp}
            creditsBalance={plan?.creditsBalance ?? 0}
          />
        ))}

        <EducationSection
          entries={educationEntries}
          onAdd={() => setEduModal({ open: true })}
          onEdit={(edu) => setEduModal({ open: true, education: edu })}
          onDelete={(edu) => deleteEducation.mutate(edu.id)}
        />

        <SkillBucketsSection repository={apiRepo} />
      </main>

      <EducationModal
        open={eduModal.open}
        onOpenChange={(open) => setEduModal((s) => ({ ...s, open }))}
        education={eduModal.education}
      />

      <TenureModal
        open={modal.open}
        onOpenChange={(open) => setModal((s) => ({ ...s, open }))}
        tenure={modal.tenure}
        title={modal.title}
        experience={modal.experience}
        tenureId={modal.tenureId}
        tenureCompany={modal.tenureCompany}
        titleId={modal.titleId}
        titleText={modal.titleText}
      />
    </div>
  );
}

// ── Capability row ─────────────────────────────────────────────────────────────
//
// Flat flex row: [⠿ shrink-0] [• shrink-0] [body flex-1] [metadata w-52] [✏🗑 shrink-0]
// Grip handle is the only drag activator — clicking body/icons never starts a drag.

interface CapabilityRowProps {
  cap: Capability;
  capIndex: number;
  justDroppedId: string | null;
  flatIndex: number;
  creditsBalance?: number;
}

function CapabilityRow({ cap, capIndex, justDroppedId, flatIndex, creditsBalance = 0 }: CapabilityRowProps) {
  const queryClient = useQueryClient();
  const [editingBody, setEditingBody] = useState(false);
  const [body, setBody] = useState(cap.body);
  const [addingSkill, setAddingSkill] = useState(false);
  const [skillInput, setSkillInput] = useState('');
  const [showRewritePanel, setShowRewritePanel] = useState(false);

  useEffect(() => {
    if (!editingBody) setBody(cap.body);
  }, [cap.body, editingBody]);

  const deleteCap = useMutation({
    mutationFn: capabilityApi.delete,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['capabilities'] });
      void queryClient.invalidateQueries({ queryKey: ['skillBuckets'] });
    },
  });
  const updateCap = useMutation({
    mutationFn: (data: UpdateCapability) => capabilityApi.update(cap.id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['capabilities'] });
      void queryClient.invalidateQueries({ queryKey: ['skillBuckets'] });
    },
  });

  const handleSaveBody   = () => {
    const t = body.trim();
    if (!t) return;
    updateCap.mutate({ body: t }, { onSuccess: () => setEditingBody(false) });
  };
  const handleCancelBody = () => { setBody(cap.body); setEditingBody(false); };

  const commitSkill = (raw: string) => {
    const s = raw.trim();
    if (s && !cap.skills.includes(s)) updateCap.mutate({ skills: [...cap.skills, s] });
    setSkillInput(''); setAddingSkill(false);
  };
  const removeSkill = (s: string) =>
    updateCap.mutate({ skills: cap.skills.filter((x) => x !== s) });

  const handleAcceptRewrite = (format: 'xyz' | 'car', suggestion: string) => {
    updateCap.mutate({ body: suggestion }, { onSuccess: () => setShowRewritePanel(false) });
  };

  const handleAdoptSkills = async (suggestedSkills: string[]) => {
    const newSkills = [...cap.skills];
    for (const skill of suggestedSkills) {
      if (!newSkills.includes(skill)) {
        newSkills.push(skill);
      }
    }
    return new Promise<void>((resolve, reject) => {
      updateCap.mutate({ skills: newSkills }, {
        onSuccess: () => resolve(),
        onError: (err) => reject(err),
      });
    });
  };

  const saving = updateCap.isPending;

  const {
    ref,
    isDragging,
  } = useSortable({
    id: cap.id,
    index: flatIndex,
    plugins: [Feedback.configure({ feedback: 'clone' })],
  });

  const isJustDropped = cap.id === justDroppedId;

  return (
    <>
      <div ref={ref} className={`group flex items-start gap-2 py-1.5 pl-2 pr-3 ${isDragging ? 'opacity-50' : ''}`}>

        {/* Drag handle */}
        <button
          className="mt-[3px] shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground/30 hover:text-muted-foreground/70 focus:outline-none transition-colors"
          tabIndex={0}
          aria-label="Drag to reorder"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>

        {/* Bullet dot + body */}
        <span className="mt-[3px] text-muted-foreground select-none text-sm shrink-0">•</span>
        <div className="flex-1 min-w-0">
          {editingBody ? (
            <>
              <textarea autoFocus
                className="w-full bg-transparent text-sm leading-relaxed resize-none outline-none border-b border-primary pb-0.5"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') handleCancelBody();
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSaveBody();
                }}
                rows={Math.max(2, body.split('\n').length)}
                disabled={saving}
              />
              {updateCap.isError && (
                <p className="text-xs text-destructive mt-1">{(updateCap.error as Error).message}</p>
              )}
              <div className="flex gap-2 mt-1.5">
                <Button size="sm" onClick={handleSaveBody} disabled={saving || !body.trim()}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
                <Button size="sm" variant="ghost" onClick={handleCancelBody} disabled={saving}>Cancel</Button>
              </div>
            </>
          ) : (
            <p className="text-sm leading-relaxed">{cap.body}</p>
          )}
        </div>

        {/* Metadata: skills + tags */}
        <div className="shrink-0 w-52 flex flex-wrap items-center gap-1">
          {cap.skills.map((s) => (
            <span key={s} className="inline-flex items-center gap-0.5 rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
              {s}
              <button type="button" onClick={() => removeSkill(s)} disabled={saving}
                className="ml-0.5 rounded-full opacity-60 hover:opacity-100 focus:outline-none disabled:pointer-events-none">
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
          {addingSkill ? (
            <input autoFocus
              className="h-5 w-20 rounded border border-input bg-background px-1.5 text-xs outline-none focus:ring-1 focus:ring-ring"
              placeholder="skill…" value={skillInput}
              onChange={(e) => setSkillInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitSkill(skillInput);
                if (e.key === 'Escape') { setSkillInput(''); setAddingSkill(false); }
              }}
              onBlur={() => commitSkill(skillInput)} disabled={saving}
            />
          ) : (
            <button type="button" onClick={() => setAddingSkill(true)}
              className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-secondary-foreground/30 px-2 py-0.5 text-xs text-muted-foreground hover:border-secondary-foreground/60 hover:text-foreground transition-colors">
              <Plus className="h-2.5 w-2.5" />Skill
            </button>
          )}
        </div>

        {/* Edit / Rewrite / delete icons */}
        <div className="shrink-0 flex items-center gap-0.5 opacity-40 group-hover:opacity-100 transition-opacity">
          <CreditGate cost={CREDIT_COSTS.bulletRewrite} balance={creditsBalance}>
            <Button variant="ghost" size="icon"
              className={`h-7 w-7 ${editingBody ? 'invisible pointer-events-none' : ''}`}
              title="Rewrite with AI" onClick={() => setShowRewritePanel(!showRewritePanel)}>
              <Sparkles className="h-3.5 w-3.5" />
            </Button>
          </CreditGate>
          <Button variant="ghost" size="icon"
            className={`h-7 w-7 ${editingBody ? 'invisible pointer-events-none' : ''}`}
            title="Edit bullet" onClick={() => setEditingBody(true)}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
            title="Delete bullet" onClick={() => {
              if (window.confirm('Delete this bullet? This cannot be undone.')) deleteCap.mutate(cap.id);
            }} disabled={deleteCap.isPending}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Rewrite suggestion panel */}
      {showRewritePanel && (
        <BulletRewritePanel
          capabilityId={cap.id}
          currentBody={cap.body}
          onAccept={handleAcceptRewrite}
          onAdoptSkills={handleAdoptSkills}
          onDismiss={() => setShowRewritePanel(false)}
        />
      )}
    </>
  );
}

// ── Experience sub-section ─────────────────────────────────────────────────────
//
// Wraps its bullet list in SortableContext so within-experience reordering works.
// Exposed handle lets parent heading rows trigger openInline / openBulk.

interface ExperienceSubSectionProps {
  experience: Experience;
  expIndex: number;
  capabilities: Capability[];
  showHeading: boolean;
  justDroppedId: string | null;
  flatIndex: number;
  flatItems: FlatItem[];
  onEditExperience: (exp: Experience) => void;
  onDeleteExperience: (exp: Experience) => void;
  creditsBalance?: number;
}

const ExperienceSubSection = forwardRef<ExperienceHandle, ExperienceSubSectionProps>(
  function ExperienceSubSection(
    { experience: exp, expIndex, capabilities, showHeading, justDroppedId, flatIndex, flatItems, onEditExperience, onDeleteExperience, creditsBalance = 0 },
    ref,
  ) {
    const queryClient = useQueryClient();

    // Drag-and-drop: this experience is a sortable item
    const {
      ref: expRef,
      isDragging,
    } = useSortable({
      id: exp.id,
      index: flatIndex,
      plugins: [Feedback.configure({ feedback: 'clone' })],
    });
    const [showInline, setShowInline] = useState(false);
    const [showBulk, setShowBulk]   = useState(false);
    const [inlineText, setInlineText] = useState('');
    const [bulkText, setBulkText]   = useState('');

    useImperativeHandle(ref, () => ({
      openInline: () => { setShowBulk(false);  setShowInline(true); },
      openBulk:   () => { setShowInline(false); setShowBulk(true);  },
    }), []);

    const createSingle = useMutation({
      mutationFn: capabilityApi.create,
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['capabilities'] });
        void queryClient.invalidateQueries({ queryKey: ['skillBuckets'] });
        setInlineText(''); setShowInline(false);
      },
    });
    const createBulk = useMutation({
      mutationFn: ({ text }: { text: string }) => capabilityApi.bulkCreate(exp.id, text),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['capabilities'] });
        void queryClient.invalidateQueries({ queryKey: ['skillBuckets'] });
        setBulkText(''); setShowBulk(false);
      },
    });

    const handleInlineSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      const body = inlineText.trim();
      if (!body) return;
      createSingle.mutate({ experienceId: exp.id, body, skills: [], evidenceLinks: [], metrics: {} });
    };
    const handleBulkSubmit = () => {
      if (!bulkText.trim()) return;
      createBulk.mutate({ text: bulkText });
    };

    const label = experienceLabel(exp);

    const addBulletButtons = (
      <>
        <Sep />
        <Button variant="ghost" size="sm"
          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => { setShowBulk(false); setShowInline(true); }}>
          <Plus className="h-3 w-3 mr-0.5" />Add bullet
        </Button>
        <Button variant="ghost" size="sm"
          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => { setShowInline(false); setShowBulk(true); }}>
          <List className="h-3 w-3 mr-0.5" />Bulk
        </Button>
      </>
    );

    return (
      <div ref={expRef} className={isDragging ? 'opacity-50' : ''}>
        {showHeading && (
          <div className="group flex items-center gap-3 pl-1 pr-3 py-2 border-t first:border-t-0">
            {/* Drag handle — only present when there's a visible heading to grab */}
            <button
              className="shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground touch-none p-1"
              tabIndex={-1}
              aria-label="Drag to reorder project"
            >
              <GripVertical className="h-3.5 w-3.5" />
            </button>
            <p className="flex-1 text-sm font-medium text-foreground min-w-0 truncate">
              {label || <span className="text-muted-foreground italic font-normal">Unnamed project</span>}
            </p>
            <div className="shrink-0 flex items-center gap-0.5">
              {addBulletButtons}
            </div>
            <div className="shrink-0 flex items-center gap-0.5 opacity-40 group-hover:opacity-100 transition-opacity">
              <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit project"
                onClick={() => onEditExperience(exp)}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
                title="Delete project" onClick={() => onDeleteExperience(exp)}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        )}

        {/* Bullet list */}
        <div className="py-0.5">
          {capabilities.map((cap) => {
            const flatItem = flatItems.find((item) => item.id === cap.id);
            return (
              <CapabilityRow
                key={cap.id}
                cap={cap}
                capIndex={capabilities.findIndex((c) => c.id === cap.id)}
                justDroppedId={justDroppedId}
                flatIndex={flatItem?.flatIndex ?? 0}
                creditsBalance={creditsBalance}
              />
            );
          })}
          {capabilities.length === 0 && !showInline && !showBulk && (
            <p className="pl-8 py-1 text-xs text-muted-foreground italic">No bullets yet</p>
          )}
        </div>

        {showInline && (
          <form onSubmit={handleInlineSubmit} className="flex items-center gap-2 px-4 py-2 border-t">
            <span className="text-muted-foreground text-sm select-none shrink-0">•</span>
            <input autoFocus
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground border-b border-input focus:border-primary transition-colors pb-0.5"
              placeholder="Type a bullet and press Enter…"
              value={inlineText}
              onChange={(e) => setInlineText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); handleInlineSubmit(e as unknown as React.FormEvent); }
                if (e.key === 'Escape') { setShowInline(false); setInlineText(''); }
              }}
              disabled={createSingle.isPending}
            />
            <Button type="submit" size="sm" disabled={createSingle.isPending || !inlineText.trim()}>
              {createSingle.isPending ? 'Adding…' : 'Add'}
            </Button>
            <Button type="button" size="sm" variant="ghost"
              onClick={() => { setShowInline(false); setInlineText(''); }}>Cancel</Button>
          </form>
        )}

        {showBulk && (
          <div className="border-t px-4 py-3 space-y-2">
            <p className="text-xs text-muted-foreground">Paste bullets — one per line.</p>
            <textarea autoFocus rows={5}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring placeholder:text-muted-foreground"
              placeholder={"Reduced latency by 40%\nLed a team of 5 engineers\nOwned on-call rotation"}
              value={bulkText} onChange={(e) => setBulkText(e.target.value)}
              disabled={createBulk.isPending}
            />
            <div className="flex gap-2 justify-end">
              <Button type="button" size="sm" variant="ghost"
                onClick={() => { setShowBulk(false); setBulkText(''); }}>Cancel</Button>
              <Button type="button" size="sm" disabled={createBulk.isPending || !bulkText.trim()}
                onClick={handleBulkSubmit}>
                {createBulk.isPending ? 'Adding…' : 'Add bullets'}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  },
);


// ── Title sub-section ──────────────────────────────────────────────────────────
//
// tenureExpRef: ref created in TenureSection. When THIS title has no visible
//   heading (single-title case), the single experience attaches to it so the
//   tenure header's buttons can fire openInline/openBulk on the right experience.
//
// titleExpRef: internal ref. When THIS title HAS a visible heading and its
//   single experience is unnamed, the title heading hosts the add-bullet buttons
//   and calls titleExpRef to trigger the forms below.

interface TitleSubSectionProps {
  title: Title;
  titleIndex: number;
  experiences: Experience[];
  capsByExp: Record<string, Capability[]>;
  showHeading: boolean;
  collapseProjects: boolean;
  companyName: string;
  justDroppedId: string | null;
  justDroppedTitleIds: Set<string>;
  flatIndex: number;
  flatItems: FlatItem[];
  tenureExpRef: React.RefObject<ExperienceHandle>;
  getExpCaps: (expId: string) => Capability[];
  onEditTitle: (t: Title) => void;
  onDeleteTitle: (t: Title, company: string) => void;
  onAddExperience: (t: Title) => void;
  onEditExperience: (exp: Experience) => void;
  onDeleteExperience: (exp: Experience) => void;
  creditsBalance?: number;
}

function TitleSubSection({
  title,
  titleIndex,
  experiences,
  capsByExp,
  showHeading,
  collapseProjects,
  companyName,
  justDroppedId,
  justDroppedTitleIds,
  flatIndex,
  flatItems,
  tenureExpRef,
  getExpCaps,
  onEditTitle,
  onDeleteTitle,
  onAddExperience,
  onEditExperience,
  onDeleteExperience,
  creditsBalance = 0,
}: TitleSubSectionProps) {
  const titleExpRef = useRef<ExperienceHandle>(null);

  // Drag-and-drop: this title is a sortable item
  const {
    ref: titleRef,
    isDragging: titleIsDragging,
  } = useSortable({
    id: title.id,
    index: flatIndex,
    plugins: [Feedback.configure({ feedback: 'clone' })],
  });

  const multiExp        = experiences.length > 1;
  const showExpHeadings = !collapseProjects && (multiExp || (experiences.length === 1 && !!experienceLabel(experiences[0]!)));
  const singleUnnamed   = !showExpHeadings && experiences.length === 1;
  const activeExpRef    = showHeading ? titleExpRef : tenureExpRef;
  const dateRange       = formatDateRange(title.startDate, title.endDate);

  const sortedExps = useMemo(
    () => [...experiences].sort((a, b) => a.sortOrder - b.sortOrder),
    [experiences],
  );


  return (
    <div ref={titleRef} className={titleIsDragging ? 'opacity-50' : ''}>
      {showHeading && (
        <div className="group flex items-start gap-3 pl-1 pr-3 py-2.5 border-t bg-muted/20">
          {/* Drag handle — only shown when there are multiple titles to reorder */}
          <button
            className="shrink-0 mt-0.5 cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground touch-none p-1"
            tabIndex={-1}
            aria-label="Drag to reorder title"
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">{title.title}</p>
            {dateRange && <p className="text-xs text-muted-foreground mt-0.5">{dateRange}</p>}
          </div>
          <div className="shrink-0 flex items-center gap-0.5">
            <Sep />
            <Button variant="ghost" size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => onAddExperience(title)}>
              <FolderPlus className="h-3.5 w-3.5 mr-0.5" />Add Project
            </Button>
            {singleUnnamed && (
              <>
                <Sep />
                <Button variant="ghost" size="sm"
                  className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => titleExpRef.current?.openInline()}>
                  <Plus className="h-3 w-3 mr-0.5" />Add bullet
                </Button>
                <Button variant="ghost" size="sm"
                  className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => titleExpRef.current?.openBulk()}>
                  <List className="h-3 w-3 mr-0.5" />Bulk
                </Button>
              </>
            )}
          </div>
          <div className="shrink-0 flex items-center gap-0.5 opacity-40 group-hover:opacity-100 transition-opacity">
            <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit title"
              onClick={() => onEditTitle(title)}><Pencil className="h-3.5 w-3.5" /></Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
              title="Delete title" onClick={() => onDeleteTitle(title, companyName)}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      )}

      {sortedExps.length === 0 ? (
        <div className="pl-8 py-1.5 pr-3 text-sm italic text-muted-foreground">
          No projects yet
        </div>
      ) : (
        <>
          {sortedExps.map((exp, i) => {
            const flatItem = flatItems.find((item) => item.id === exp.id);
            return (
              <ExperienceSubSection
                key={exp.id}
                ref={singleUnnamed && i === 0 ? activeExpRef : null}
                experience={exp}
                expIndex={i}
                capabilities={getExpCaps(exp.id)}
                showHeading={showExpHeadings}
                justDroppedId={justDroppedId}
                flatIndex={flatItem?.flatIndex ?? 0}
                flatItems={flatItems}
                onEditExperience={onEditExperience}
                onDeleteExperience={onDeleteExperience}
                creditsBalance={creditsBalance}
              />
            );
          })}
        </>
      )}
    </div>
  );
}

// ── Tenure section ─────────────────────────────────────────────────────────────
//
// A single DndContext covers all drag types within this tenure:
//   • Title drag    — reorders titles (via title SortableContext)
//   • Experience drag — can drop on other experiences OR directly on title headers
//   • Capability drag — reorders within experience OR adopts into new experience
//
// Two nested SortableContexts (tree-list pattern):
//   1. Titles: [title1, title2, ...] — for reordering titles
//   2. Experiences (flattened): [title1, exp1, exp2, title2, exp3, ...] — for collision detection
//
// Key insight: allFlatItems is ONLY for SortableContext registration (collision detection).
// Rendering remains GROUPED by title (TitleSubSection contains its experiences).
// This keeps dnd-kit happy during drag operations (stable rendering) while allowing
// title headers to be drop targets (registered in experience context).
//
// Title headers are registered in BOTH contexts:
//   - In title context → can reorder titles
//   - In experience context → become valid drop targets for experiences
//
// Drop detection uses metadata (titleId) to determine when to move across titles.
// onDragEnd dispatches by type using titleSet / expSet / capToExp membership.
//
// Header row right:
//   [edit][delete] | [+ Add Project] [? + Add bullet + Bulk] | [+ Add Title]

interface TenureSectionProps {
  tenure: Tenure;
  titles: Title[];
  expsByTitle: Record<string, Experience[]>;
  capsByExp: Record<string, Capability[]>;
  collapseTitles: boolean;
  collapseProjects: boolean;
  onEditTenure: (t: Tenure, title?: Title) => void;
  onDeleteTenure: (t: Tenure) => void;
  onAddTitle: (t: Tenure) => void;
  onEditTitle: (t: Title) => void;
  onDeleteTitle: (t: Title, company: string) => void;
  onAddExperience: (t: Title) => void;
  onEditExperience: (exp: Experience) => void;
  onDeleteExperience: (exp: Experience) => void;
  creditsBalance?: number;
}

function TenureSection({
  tenure, titles, expsByTitle, capsByExp,
  collapseTitles, collapseProjects,
  onEditTenure, onDeleteTenure, onAddTitle,
  onEditTitle, onDeleteTitle,
  onAddExperience, onEditExperience, onDeleteExperience,
  creditsBalance = 0,
}: TenureSectionProps) {
  const queryClient = useQueryClient();
  const tenureExpRef = useRef<ExperienceHandle>(null);
  const [justDroppedId, setJustDroppedId] = useState<string | null>(null);
  const [justDroppedTitleIds, setJustDroppedTitleIds] = useState<Set<string>>(new Set());
  const snapshotRef = useRef<{ titles: Title[]; allCaps: Capability[] }>();

  // ── Sensor configuration ───────────────────────────────────────────────────
  const sensors = useMemo(() => [
    PointerSensor.configure({
      activatorElements(source) {
        return [source.element, source.handle];
      },
    }),
    KeyboardSensor,
  ], []);

  // ── Lookup structures ──────────────────────────────────────────────────────

  const sortedTitles = useMemo(
    () => [...titles].sort((a, b) => a.sortOrder - b.sortOrder),
    [titles],
  );

  const titleSet = useMemo(() => new Set(sortedTitles.map((t) => t.id)), [sortedTitles]);

  // Build flat item array: [title1, exp1, exp2, title2, exp3, cap1, cap2, ...]
  // Each item gets a flat index for drag operations
  const flatItems = useMemo(() => {
    const items: FlatItem[] = [];
    let flatIndex = 0;

    sortedTitles.forEach((title) => {
      items.push({
        id: title.id,
        type: 'title',
        flatIndex: flatIndex++,
      });

      const titleExps = (expsByTitle[title.id] ?? []).sort((a, b) => a.sortOrder - b.sortOrder);
      titleExps.forEach((exp) => {
        items.push({
          id: exp.id,
          type: 'experience',
          flatIndex: flatIndex++,
          parentId: title.id,
        });

        const expCaps = (capsByExp[exp.id] ?? []).sort((a, b) => a.sortOrder - b.sortOrder);
        expCaps.forEach((cap) => {
          items.push({
            id: cap.id,
            type: 'capability',
            flatIndex: flatIndex++,
            parentId: exp.id,
          });
        });
      });
    });

    return items;
  }, [sortedTitles, expsByTitle, capsByExp]);

  const itemById = useMemo(
    () => new Map(flatItems.map((item) => [item.id, item])),
    [flatItems],
  );

  const allTenureExps = useMemo(
    () => sortedTitles.flatMap((t) => expsByTitle[t.id] ?? []),
    [sortedTitles, expsByTitle],
  );
  const expSet  = useMemo(() => new Set(allTenureExps.map((e) => e.id)), [allTenureExps]);
  const expById = useMemo(() => new Map(allTenureExps.map((e) => [e.id, e])), [allTenureExps]);

  const allTenureCaps = useMemo(
    () => allTenureExps.flatMap((e) => capsByExp[e.id] ?? []),
    [allTenureExps, capsByExp],
  );
  const capById  = useMemo(() => new Map(allTenureCaps.map((c) => [c.id, c])), [allTenureCaps]);
  const capToExp = useMemo(() => new Map(allTenureCaps.map((c) => [c.id, c.experienceId])), [allTenureCaps]);

  // ── Mutations ──────────────────────────────────────────────────────────────

  const titleReorderMutation = useMutation({
    mutationFn: (reorders: ReorderTitleItem[]) => titleApi.reorder(reorders),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['titles'] }),
  });

  const expReorderMutation = useMutation({
    mutationFn: (reorders: ReorderExperienceItem[]) => experienceApi.reorder(reorders),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['experiences'] }),
  });

  const capReorderMutation = useMutation({
    mutationFn: (reorders: ReorderItem[]) => capabilityApi.reorder(reorders),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['capabilities'] });
      void queryClient.invalidateQueries({ queryKey: ['skillBuckets'] });
    },
  });

  // ── Drag helpers ──────────────────────────────────────────────────────────

  // Helper: compute sort orders from flat array
  const computeSortOrders = useCallback((finalFlat: FlatItem[]) => {
    const titleSortOrders = new Map<string, number>();
    const expSortOrders = new Map<string, number>();
    const capSortOrders = new Map<string, number>();

    let titleIdx = 0;
    let expIdx = 0;
    let capIdx = 0;

    finalFlat.forEach((item) => {
      if (item.type === 'title') {
        titleSortOrders.set(item.id, titleIdx++);
        expIdx = 0;
      } else if (item.type === 'experience') {
        expSortOrders.set(item.id, expIdx++);
        capIdx = 0;
      } else if (item.type === 'capability') {
        capSortOrders.set(item.id, capIdx++);
      }
    });

    return { titleSortOrders, expSortOrders, capSortOrders };
  }, []);

  // Single source of truth: the flat array. Render from this, update this during drag.
  const [items, setItems] = useState(flatItems);
  const snapshot = useRef(flatItems);

  // Update items state when flatItems changes (from queryClient data)
  useEffect(() => {
    setItems(flatItems);
    snapshot.current = flatItems;
  }, [flatItems]);

  // Derive children from items state (not queryClient) so rendering reflects drag changes
  const getTitleChildren = useCallback((titleId: string): Experience[] => {
    return items
      .filter(item => item.type === 'experience' && item.parentId === titleId)
      .map(item => expById.get(item.id))
      .filter(Boolean) as Experience[];
  }, [items, expById]);

  const getExpCaps = useCallback((expId: string): Capability[] => {
    return items
      .filter(item => item.type === 'capability' && item.parentId === expId)
      .map(item => capById.get(item.id))
      .filter(Boolean) as Capability[];
  }, [items, capById]);

  const handleDragStart = useCallback<DragDropEventHandlers['onDragStart']>(() => {
    snapshot.current = structuredClone(items);
  }, [items]);

  const handleDragOver = useCallback<DragDropEventHandlers['onDragOver']>((event) => {
    const { source, target } = event.operation;
    if (!source || !target) return;

    const sourceId = String(source.id);
    const targetId = String(target.id);

    const sourceIdx = items.findIndex((item) => item.id === sourceId);
    const targetIdx = items.findIndex((item) => item.id === targetId);

    if (sourceIdx === -1 || targetIdx === -1 || sourceIdx === targetIdx) return;

    const sourceItem = items[sourceIdx]!;
    const targetItem = items[targetIdx]!;

    // Enforce hierarchy constraints: items can only move within their parent group
    // - Capabilities can only move within same experience (parentId must match)
    // - Experiences can only move within same title (parentId must match)
    // - Titles can only move within same tenure (no parent check needed, tenure-scoped)
    if (sourceItem.type === 'capability' && targetItem.type === 'capability') {
      // Both capabilities must belong to same experience
      if (sourceItem.parentId !== targetItem.parentId) return;
    } else if (sourceItem.type === 'experience' && targetItem.type === 'experience') {
      // Both experiences must belong to same title
      if (sourceItem.parentId !== targetItem.parentId) return;
    } else if (sourceItem.type !== targetItem.type) {
      // Can't drag across different types (cap onto exp, exp onto title, etc)
      return;
    }

    setItems((current) => arrayMove(current, sourceIdx, targetIdx));
  }, [items]);

  const handleDragEnd = useCallback<DragDropEventHandlers['onDragEnd']>((event) => {
    if (event.canceled) {
      setItems(snapshot.current);
      return;
    }

    if (!event.operation?.target || event.operation?.source?.id === event.operation?.target?.id) return;

    const activeId = String(event.operation?.source?.id);
    const { titleSortOrders, expSortOrders, capSortOrders } = computeSortOrders(items);

    // ── Title reorder ──────────────────────────────────────────────────────
    if (titleSet.has(activeId)) {
      const reorders: ReorderTitleItem[] = Array.from(titleSortOrders).map(([id, order]) => ({
        id,
        sortOrder: order,
      }));

      if (reorders.some((r) => {
        const old = sortedTitles.find((t) => t.id === r.id);
        return old && old.sortOrder !== r.sortOrder;
      })) {
        setJustDroppedId(activeId);
        titleReorderMutation.mutate(reorders);
        requestAnimationFrame(() => setJustDroppedId(null));
      }
      return;
    }

    // ── Experience reorder ────────────────────────────────────────────────────
    if (expSet.has(activeId)) {
      const reorders: ReorderExperienceItem[] = Array.from(expSortOrders).map(([id, order]) => ({
        id,
        sortOrder: order,
      }));

      if (reorders.some((r) => {
        const old = allTenureExps.find((e) => e.id === r.id);
        return old && old.sortOrder !== r.sortOrder;
      })) {
        setJustDroppedId(activeId);
        expReorderMutation.mutate(reorders);
        requestAnimationFrame(() => setJustDroppedId(null));
      }
      return;
    }

    // ── Capability reorder ────────────────────────────────────────────────────
    if (capById.has(activeId)) {
      const reorders: ReorderItem[] = Array.from(capSortOrders).map(([id, order]) => ({
        id,
        sortOrder: order,
      }));

      if (reorders.some((r) => {
        const old = allTenureCaps.find((c) => c.id === r.id);
        return old && old.sortOrder !== r.sortOrder;
      })) {
        setJustDroppedId(activeId);
        capReorderMutation.mutate(reorders);
        requestAnimationFrame(() => setJustDroppedId(null));
      }
    }
  }, [
    titleSet, expSet, capById,
    sortedTitles, allTenureExps, allTenureCaps,
    computeSortOrders, items,
    titleReorderMutation, expReorderMutation, capReorderMutation,
  ]);

  // ── Derived display state ──────────────────────────────────────────────────

  const singleTitle = sortedTitles.length === 1;
  const multiTitles = sortedTitles.length > 1;
  const firstTitle  = sortedTitles[0];
  const firstTitleExps = firstTitle ? (expsByTitle[firstTitle.id] ?? []) : [];

  const firstTitleShowExpHeadings =
    firstTitleExps.length > 1 ||
    (firstTitleExps.length === 1 && !!experienceLabel(firstTitleExps[0]!));
  const tenureSingleUnnamed =
    singleTitle &&
    firstTitleExps.length === 1 &&
    !firstTitleShowExpHeadings;

  const latest    = latestTitle(sortedTitles);
  const dateRange = formatDateRange(tenure.startDate, tenure.endDate);


  return (
    <DragDropProvider
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="rounded-lg border bg-card">
        {/* ── Tenure header ─────────────────────────────────────────────── */}
        <div className="group flex items-start gap-3 pl-4 pr-3 py-3 border-b">
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <h2 className="font-semibold text-base leading-snug">{tenure.company}</h2>
              {latest && (
                <span className="text-sm text-muted-foreground font-normal">· {latest.title}</span>
              )}
            </div>
            {dateRange && <p className="text-sm text-muted-foreground mt-0.5">{dateRange}</p>}
          </div>

          <div className="shrink-0 flex items-center gap-0.5">
            <Sep />
            {singleTitle && firstTitle && (
              <Button variant="ghost" size="sm"
                className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => onAddExperience(firstTitle)}>
                <FolderPlus className="h-3.5 w-3.5 mr-0.5" />Add Project
              </Button>
            )}
            {tenureSingleUnnamed && (
              <>
                <Button variant="ghost" size="sm"
                  className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => tenureExpRef.current?.openInline()}>
                  <Plus className="h-3.5 w-3.5 mr-0.5" />Add bullet
                </Button>
                <Button variant="ghost" size="sm"
                  className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => tenureExpRef.current?.openBulk()}>
                  <List className="h-3.5 w-3.5 mr-0.5" />Bulk
                </Button>
              </>
            )}
            <Sep />
            <Button variant="ghost" size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => onAddTitle(tenure)}>
              <Briefcase className="h-3.5 w-3.5 mr-0.5" />Add Title
            </Button>
          </div>

          <div className="shrink-0 flex items-center gap-0.5 opacity-40 group-hover:opacity-100 transition-opacity">
            <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit company"
              onClick={() => onEditTenure(tenure, tenureSingleUnnamed ? firstTitle : undefined)}><Pencil className="h-3.5 w-3.5" /></Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
              title="Delete company" onClick={() => onDeleteTenure(tenure)}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        </div>

        {/* ── Title sub-sections ────────────────────────────────────────────── */}
        {sortedTitles.length === 0 ? (
          <p className="pl-8 py-3 text-sm text-muted-foreground italic">No titles yet</p>
        ) : (
          <>
            {sortedTitles.map((t, titleIdx) => {
              const flatItem = items.find((item) => item.id === t.id);
              return (
                <TitleSubSection
                  key={t.id}
                  title={t}
                  titleIndex={titleIdx}
                  experiences={getTitleChildren(t.id)}
                  capsByExp={capsByExp}
                  showHeading={!collapseTitles && multiTitles}
                  collapseProjects={collapseProjects}
                  companyName={tenure.company}
                  justDroppedId={justDroppedId}
                  justDroppedTitleIds={justDroppedTitleIds}
                  flatIndex={flatItem?.flatIndex ?? 0}
                  flatItems={items}
                  tenureExpRef={tenureExpRef}
                  getExpCaps={getExpCaps}
                  onEditTitle={onEditTitle}
                  onDeleteTitle={onDeleteTitle}
                  onAddExperience={onAddExperience}
                  onEditExperience={onEditExperience}
                  onDeleteExperience={onDeleteExperience}
                  creditsBalance={creditsBalance}
                />
              );
            })}
          </>
        )}
      </div>
    </DragDropProvider>
  );
}

// ── Education section ─────────────────────────────────────────────────────────
//
// Flat sortable list of education entries rendered below the experience sections.

interface EducationSectionProps {
  entries: Education[];
  onAdd: () => void;
  onEdit: (edu: Education) => void;
  onDelete: (edu: Education) => void;
}

function EducationRow({
  edu,
  eduIndex,
  onEdit,
  onDelete,
  justDroppedId,
}: {
  edu: Education;
  eduIndex: number;
  onEdit: (edu: Education) => void;
  onDelete: (edu: Education) => void;
  justDroppedId: string | null;
}) {
  const { ref, isDragging } = useSortable({ id: edu.id, index: eduIndex });

  const isJustDropped = justDroppedId === edu.id;
  const year = edu.graduationDate ? edu.graduationDate.slice(0, 4) : 'In Progress';

  return (
    <div
      ref={ref}
      className={[
        'group flex items-start gap-2 rounded-md border bg-card px-3 py-2.5 mb-2',
        'transition-shadow',
        isDragging ? 'shadow-lg z-50 relative opacity-40' : '',
        isJustDropped ? 'ring-2 ring-primary/40' : '',
      ].join(' ')}
    >
      {/* drag handle */}
      <button
        className="mt-0.5 cursor-grab text-muted-foreground/40 hover:text-muted-foreground focus:outline-none"
        tabIndex={-1}
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold truncate">
          {edu.degreeAndMajor} <span className="font-normal text-muted-foreground">({year})</span>
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">{edu.institution}</p>
      </div>

      {/* actions */}
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit education"
          onClick={() => onEdit(edu)}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive/70 hover:text-destructive"
          title="Delete education" onClick={() => onDelete(edu)}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

function EducationSection({ entries, onAdd, onEdit, onDelete }: EducationSectionProps) {
  const queryClient = useQueryClient();
  const [sorted, setSorted] = useState<Education[]>(entries);
  const [justDroppedId, setJustDroppedId] = useState<string | null>(null);

  // Keep local sorted state in sync with query data
  useEffect(() => { setSorted(entries); }, [entries]);

  const reorderMutation = useMutation({
    mutationFn: (reorders: ReorderEducationItem[]) => educationApi.reorder(reorders),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['education'] }),
  });

  const handleDragEnd = useCallback((event: any) => {
    if (!event.operation?.target || event.operation?.source?.id === event.operation?.target?.id) return;

    const oldIdx = sorted.findIndex((e) => e.id === event.operation?.source?.id);
    const newIdx = sorted.findIndex((e) => e.id === event.operation?.target?.id);
    if (oldIdx === -1 || newIdx === -1) return;

    const reordered = arrayMove(sorted, oldIdx, newIdx);
    const reorders: ReorderEducationItem[] = reordered.map((e, i) => ({ id: e.id, sortOrder: i }));

    setJustDroppedId(event.operation?.source?.id as string);
    queryClient.setQueryData<Education[]>(['education'], (old) => {
      if (!old) return old;
      const orderMap = new Map(reorders.map((r) => [r.id, r.sortOrder]));
      return old.map((e) => orderMap.has(e.id) ? { ...e, sortOrder: orderMap.get(e.id)! } : e);
    });
    reorderMutation.mutate(reorders);
    requestAnimationFrame(() => setJustDroppedId(null));
  }, [sorted, queryClient, reorderMutation]);

  return (
    <div className="rounded-lg border bg-card/50 p-4">
      {/* Section header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <GraduationCap className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-semibold text-base">Education</h2>
        </div>
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onAdd}>
          <Plus className="h-3.5 w-3.5 mr-0.5" />Add education
        </Button>
      </div>

      {sorted.length === 0 && (
        <div className="py-8 text-center">
          <p className="text-sm text-muted-foreground">No education entries yet</p>
          <Button variant="ghost" size="sm" className="mt-2 text-xs" onClick={onAdd}>
            <Plus className="h-3.5 w-3.5 mr-0.5" />Add education
          </Button>
        </div>
      )}

      {sorted.length > 0 && (
        <DragDropProvider onDragEnd={handleDragEnd}>
          <div className="divide-y-0">
            {sorted.map((edu, idx) => (
              <EducationRow
                key={edu.id}
                edu={edu}
                eduIndex={idx}
                onEdit={onEdit}
                onDelete={onDelete}
                justDroppedId={justDroppedId}
              />
            ))}
          </div>
        </DragDropProvider>
      )}
    </div>
  );
}
